import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import {
  FaArrowLeft,
  FaUsers,
  FaComments,
  FaHeart,
  FaLayerGroup,
  FaChartLine,
  FaRobot,
  FaUser,
  FaCheckCircle,
  FaExclamationCircle,
  FaClock,
  FaFlask,
  FaNetworkWired,
  FaDownload,
  FaFileCsv,
} from "react-icons/fa";
import api from "../../services/api";

const COLORS = {
  pro: "#3b82f6",
  neutral: "#6b7280",
  contra: "#ef4444",
  human: "#22c55e",
  ai: "#9333ea",
  active: "#22c55e",
  ready: "#3b82f6",
  completed: "#6b7280",
  error: "#ef4444",
};

const ConditionAnalytics = () => {
  const { conditionId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const adminToken = localStorage.getItem("admin_token");

  const fetchData = useCallback(async () => {
    if (!adminToken || !conditionId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/admin/conditions/${conditionId}/analytics`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        params: {},
      });
      setData(response.data);
    } catch (err) {
      console.error("Error fetching condition analytics:", err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [adminToken, conditionId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const exportData = async (format = "json", humanOnly = false) => {
    try {
      const params = new URLSearchParams({ format });
      if (humanOnly) params.append("humanOnly", "true");

      const response = await api.get(
        `/admin/conditions/${conditionId}/export?${params}`,
        {
          headers: { Authorization: `Bearer ${adminToken}` },
          responseType: format === "csv" ? "text" : "json",
        }
      );

      const blob = new Blob(
        [format === "csv" ? response.data : JSON.stringify(response.data, null, 2)],
        { type: format === "csv" ? "text/csv" : "application/json" }
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `spiders-${conditionId}-${new Date().toISOString().split("T")[0]}.${format}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
      setError("Failed to export data");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-400 hover:text-white mb-4"
          >
            <FaArrowLeft className="mr-2" /> Back
          </button>
          <div className="bg-red-900/50 text-red-200 p-4 rounded-lg">
            Error: {error}
          </div>
        </div>
      </div>
    );
  }

  // Prepare pie chart data
  const userTypePieData = [
    { name: "Human", value: data?.users?.human || 0, color: COLORS.human },
    { name: "AI", value: data?.users?.ai || 0, color: COLORS.ai },
  ].filter(d => d.value > 0);

  const opinionPieData = [
    { name: "Pro", value: data?.users?.opinionDistribution?.pro || 0, color: COLORS.pro },
    { name: "Neutral", value: data?.users?.opinionDistribution?.neutral || 0, color: COLORS.neutral },
    { name: "Contra", value: data?.users?.opinionDistribution?.contra || 0, color: COLORS.contra },
  ].filter(d => d.value > 0);

  const instanceStatusData = [
    { name: "Active", value: data?.instances?.summary?.active || 0, color: COLORS.active },
    { name: "Ready", value: data?.instances?.summary?.ready || 0, color: COLORS.ready },
    { name: "Completed", value: data?.instances?.summary?.completed || 0, color: COLORS.completed },
    { name: "Error", value: data?.instances?.summary?.error || 0, color: COLORS.error },
  ].filter(d => d.value > 0);

  const getStatusIcon = (status) => {
    switch (status) {
      case "active": return <FaCheckCircle className="text-green-400" />;
      case "ready": return <FaClock className="text-blue-400" />;
      case "completed": return <FaCheckCircle className="text-gray-400" />;
      case "error": return <FaExclamationCircle className="text-red-400" />;
      default: return <FaClock className="text-gray-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <button
              onClick={() => navigate("/admin/dashboard")}
              className="flex items-center text-gray-400 hover:text-white mr-4"
            >
              <FaArrowLeft className="mr-2" /> Back to Dashboard
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">{data?.condition?.name}</h1>
              <p className="text-gray-400 text-sm">
                {data?.condition?.description || "No description"} | Topic: {data?.condition?.topic?.name || "Not set"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-2">
              <button
                onClick={() => exportData("json")}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                title="Export all data as JSON"
              >
                <FaDownload /> JSON
              </button>
              <button
                onClick={() => exportData("csv")}
                className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-sm"
                title="Export all data as CSV"
              >
                <FaFileCsv /> CSV
              </button>
              <button
                onClick={() => exportData("json", true)}
                className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm"
                title="Export only human participant data"
              >
                <FaUser /> Human Only
              </button>
            </div>
            <span
              className={`px-3 py-1 rounded-full text-sm ${
                data?.condition?.status === "active"
                  ? "bg-green-900 text-green-200"
                  : data?.condition?.status === "ready"
                  ? "bg-blue-900 text-blue-200"
                  : data?.condition?.status === "archived"
                  ? "bg-red-900 text-red-200"
                  : "bg-gray-700 text-gray-300"
              }`}
            >
              {data?.condition?.status?.toUpperCase() || "UNKNOWN"}
            </span>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <MetricCard
            icon={<FaLayerGroup />}
            label="Instances"
            value={data?.instances?.summary?.total || 0}
            subtext={`${data?.instances?.summary?.active || 0} active`}
            color="blue"
          />
          <MetricCard
            icon={<FaUsers />}
            label="Total Users"
            value={data?.users?.total || 0}
            subtext={`${data?.users?.human || 0} human`}
            color="green"
          />
          <MetricCard
            icon={<FaComments />}
            label="Messages"
            value={data?.content?.messages?.total || 0}
            subtext={`${data?.content?.messages?.byHuman || 0} by humans`}
            color="purple"
          />
          <MetricCard
            icon={<FaHeart />}
            label="Total Likes"
            value={data?.content?.messages?.totalLikes || 0}
            subtext={`${data?.engagement?.avgLikesPerMessage || 0} avg/msg`}
            color="pink"
          />
          <MetricCard
            icon={<FaComments />}
            label="Comments"
            value={data?.content?.comments?.total || 0}
            subtext={`${data?.engagement?.avgCommentsPerMessage || 0} avg/msg`}
            color="yellow"
          />
          <MetricCard
            icon={<FaFlask />}
            label="Test Instances"
            value={data?.instances?.summary?.test || 0}
            subtext="for testing"
            color="orange"
          />
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Instance Status Distribution */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-white font-semibold mb-4">Instance Status</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={instanceStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                    labelLine={false}
                  >
                    {instanceStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* User Distribution */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-white font-semibold mb-4">User Types</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={userTypePieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                    labelLine={false}
                  >
                    {userTypePieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Opinion Distribution */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-white font-semibold mb-4">Opinion Distribution</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={opinionPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                    labelLine={false}
                  >
                    {opinionPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Activity Timeline */}
        {data?.activityTimeline?.length > 0 && (
          <div className="bg-gray-800 rounded-lg p-4 mb-6">
            <h2 className="text-lg font-semibold text-white flex items-center mb-4">
              <FaChartLine className="mr-2" /> Activity Over Time
            </h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.activityTimeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="date"
                    stroke="#9ca3af"
                    tick={{ fill: "#9ca3af", fontSize: 10 }}
                  />
                  <YAxis stroke="#9ca3af" tick={{ fill: "#9ca3af" }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1f2937",
                      border: "none",
                      borderRadius: "8px",
                    }}
                    labelStyle={{ color: "#fff" }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="messages"
                    stroke="#3b82f6"
                    name="Messages"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="comments"
                    stroke="#22c55e"
                    name="Comments"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="likes"
                    stroke="#ec4899"
                    name="Likes"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Instances List */}
        <div className="bg-gray-800 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-white flex items-center mb-4">
            <FaLayerGroup className="mr-2" /> Instances ({data?.instances?.list?.length || 0})
          </h2>

          {data?.instances?.list?.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No instances created yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-700">
                    <th className="pb-3 pr-4">Status</th>
                    <th className="pb-3 pr-4">Participant</th>
                    <th className="pb-3 pr-4">Users</th>
                    <th className="pb-3 pr-4">Messages</th>
                    <th className="pb-3 pr-4">Comments</th>
                    <th className="pb-3 pr-4">Created</th>
                    <th className="pb-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.instances?.list?.map((instance) => (
                    <tr key={instance.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(instance.status)}
                          <span className="text-white capitalize">{instance.status}</span>
                          {instance.isTestInstance && (
                            <span className="text-xs bg-yellow-600 text-yellow-100 px-1.5 py-0.5 rounded">TEST</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-gray-300">{instance.participantId || "—"}</td>
                      <td className="py-3 pr-4">
                        <span className="text-white">{instance.users}</span>
                        <span className="text-gray-500 text-xs ml-1">({instance.humans} human)</span>
                      </td>
                      <td className="py-3 pr-4 text-white">{instance.messages}</td>
                      <td className="py-3 pr-4 text-white">{instance.comments}</td>
                      <td className="py-3 pr-4 text-gray-400 text-xs">
                        {new Date(instance.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3">
                        <button
                          onClick={() => navigate(`/admin/instances/${instance.id}/analytics`)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs"
                        >
                          <FaNetworkWired /> View Analytics
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper Components
const MetricCard = ({ icon, label, value, subtext, color }) => {
  const colorClasses = {
    blue: "bg-blue-900/50 text-blue-400",
    green: "bg-green-900/50 text-green-400",
    pink: "bg-pink-900/50 text-pink-400",
    purple: "bg-purple-900/50 text-purple-400",
    yellow: "bg-yellow-900/50 text-yellow-400",
    orange: "bg-orange-900/50 text-orange-400",
  };

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-400 text-xs">{label}</p>
          <p className="text-xl font-bold text-white">{value}</p>
          {subtext && <p className="text-gray-500 text-xs">{subtext}</p>}
        </div>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>{icon}</div>
      </div>
    </div>
  );
};

export default ConditionAnalytics;
